import React from 'react';
import PropTypes from 'prop-types';

import {connectModuleEnv} from '../../oe-module-env';
import {frefFromRef, frefToRef} from '../../../lib/react/higher-order-components';
import {oeInterfaceManager} from '../../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../../react-oe/oe-interface-adapter';
import OEResizeObserver from '../../../lib/components/oe-resize-observer';
import OEPopover from '../../views/oe-popover';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import retardUpdate from '../../../lib/react/update-retarder';

export class OENotePopover extends React.PureComponent {

    constructor(props) {
        super(props);

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.id = typeof(this.props.id) === 'number' ? this.props.id : null;
        this.rect = null;

        this.unitSize = 16;
        this.pinned = true;
        
        this.state = {
            rect: this.rect,
            unitSize: this.unitSize,
            pinned: this.pinned
        };

        this.onNoteRectChanged = this.onNoteRectChanged.bind(this);

        this.onPinnedChanged = this.onPinnedChanged.bind(this);

        this.onResize = this.onResize.bind(this);
        this.onUnitSizeResize = this.onUnitSizeResize.bind(this);

        this.onContentRef = this.onContentRef.bind(this);
    }

    componentDidMount() {
        this.mounted = true;
    }

    componentWillUnmount()  {
        if(this._resizeTimer)   clearTimeout(this._resizeTimer);
        this._resizeTimer = null;
        this.mounted = false;
    }

    componentWillReceiveProps(nextProps) {
        if(nextProps.id !== this.props.id)  {
            this.id = typeof(nextProps.id) === 'number' ? nextProps.id : null;
        }

        if(nextProps.isOpen !== this.props.isOpen || nextProps.id !== this.props.id)     {
            if(nextProps.isOpen || typeof(nextProps.id) === 'number')  this.updateRect(nextProps);
        }

        if(nextProps.rectUpdateEnabled && nextProps.rectUpdateEnabled !== this.props.rectUpdateEnabled)     {
           this.updateRectIfNeeded();
        }
    }

    componentDidUpdate(prevProps, prevState)    {
        if(this.props.onRectDidUpdate && !OEToolbox.shallowEqual(this.state.rect, prevState.rect))    {
            this.props.onRectDidUpdate(this, this.state.rect);
        }
    }

    onConnect()  {
        this.oe.sharedNotificationCenter.register(this.oe.NotificationName.noteRectChanged, this.onNoteRectChanged);
    }

    onRelease()    {
        this.oe.sharedNotificationCenter.unregister(this.oe.NotificationName.noteRectChanged, this.onNoteRectChanged);
    }

    setRect(rect){
        if(OEToolbox.shallowEqual(rect, this.rect)) return;
        this.rect = rect;
        this.setState({rect: this.rect});
    }

    updateRect(props)    {
        props = props || this.props;

        this.needsRectUpdate = false;

        if(!this.oe.isReady() || typeof(props.id) !== 'number') return;

        let noteRect = this.oe.sharedInterface.getUIControllerNote().getNoteRect(props.id);
        let rect = {x: noteRect.x, y: noteRect.y, w: noteRect.z, h: noteRect.w};
        let devicePR = 1 / this.oe.Module.devicePixelRatio();
        rect = {x: rect.x * devicePR, y: rect.y * devicePR, w: rect.w * devicePR, h: rect.h * devicePR};

        this.setRect(rect);
    }

    updateRectIfNeeded(props)    {
        if(!this.needsRectUpdate)   return;
        this.updateRect();
    }

    updateSizeConstrains()  {
        let sizeConstraints;
        
        if(this.contentRef) {
            let rect = this.contentRef.getBoundingClientRect();
            let windowSize = {w: window.innerWidth || document.documentElement.clientWidth, h: window.innerHeight || document.documentElement.clientHeight};
            let pad = this.pinned ? 0.25 * this.unitSize + 1 : 2;

            sizeConstraints = {w: windowSize.w - rect.left - pad, h: windowSize.h - rect.top - pad};

            let boundariesElement = this.props.boundariesElement;
            if(typeof(boundariesElement) === 'object' && boundariesElement.getBoundingClientRect()) {
                let rect_ = boundariesElement.getBoundingClientRect();
            
                sizeConstraints = {w: Math.min(sizeConstraints.w, rect_.right - rect.left - pad), h: Math.min(sizeConstraints.h, rect_.bottom - rect.top - pad)};
            }
        }

        if(!OEToolbox.shallowEqual(this.sizeConstraints, sizeConstraints)) {
            this.sizeConstraints = sizeConstraints;
            this.setState({sizeConstraints: sizeConstraints});
        }
    }

    updateState(released)   {
        retardUpdate(this, () => {
            if(!this.oe.isReady() || released === true)   {
                this.rect = null;
                this.setState({rect: this.rect});
                this.needsRectUpdate = false;
                return;
            }

            this.updateRect();
        });
    }

    onNoteRectChanged(message, userInfo) {
        if(userInfo.ID !== this.id)  return;

        if(!this.props.rectUpdateEnabled)    {
            this.needsRectUpdate = true;
            return;
        }

        this.updateRect();
    }
    
    onPinnedChanged(sender, pinned) {
        this.pinned = pinned;
        this.setState({pinned: pinned});
        this.updateSizeConstrains();
        if(this.props.onPinnedChanged)  this.props.onPinnedChanged(this, pinned);
    }

    onResize(sender, size)  {
        if(!this.mounted || size.w == 0 || size.h == 0 || OEToolbox.shallowEqual(this.size, size))   {
            if(this.props.onResize) this.props.onResize(this,size);
            return;
        }

        this.size = size;
        this.updateSizeConstrains();

        //
        if(this._resizeTimer)   clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => { 
            this._resizeTimer = null;
            this.setState({size: size});                // setting size state deferred prevents "ResizeObserver loop completed with undelivered notifications." error
        }, 333);

        //
        this.forceUpdate();

        //
        if(this.props.onResize) this.props.onResize(this,size);
    }

    onUnitSizeResize(sender, size)  {
        if(this.unitSize == size.w) return;
        this.unitSize = size.w;
        this.setState({unitSize: size.w});
        this.updateSizeConstrains();
    }

    onContentRef(ref)   {
        this.contentRef = ref;
        this.updateSizeConstrains();
        if(this.props.onContentRef) this.props.onContentRef(this, ref);
    }

    render() {
        let yOffset = 2.625 - (this.state.pinned ? this.props.offset.y / this.state.unitSize : 0);

        let style = {maxWidth: 'calc(100vw - 1em)', maxHeight: 'calc(100vh - ' + yOffset + 'em)'};

        if(this.props.resizeable)   {
            style.resize = 'both';

            if(this.state.size) {
                style.width = this.state.size.w;
                style.height = this.state.size.h;
            }
    
            if(this.state.sizeConstraints)  {
                style.maxWidth = 'min(' + this.state.sizeConstraints.w / this.state.unitSize + 'em, ' + style.maxWidth + ')'
                style.maxHeight = 'min(' + this.state.sizeConstraints.h / this.state.unitSize + 'em, ' + style.maxHeight + ')'
            }
        }

        //
        const rect = !!this.state.rect ? {x: this.state.rect.x + this.props.offset.x, y: this.state.rect.y + this.props.offset.y, w: this.state.rect.w, h: this.state.rect.h} : null;

        //
        const {position, rect: rect_, offset, isOpen, onContentRef, resizeable, onPinnedChanged, id, rectUpdateEnabled, onRectDidUpdate, onResize, children, ...popoverProps} = this.props;

        return (
            <React.Fragment>
                <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>

                <OEPopover
                    {...popoverProps}
                    rect={rect}
                    isOpen={this.props.isOpen && !!rect}
                    onPinnedChanged={this.onPinnedChanged}
                >

                    <div className={this.props.contentClassName} style={style} ref={this.onContentRef}>
                        {this.props.resizeable ? <OEResizeObserver onResize={this.onResize}/> : null}

                        <OEResizeObserver className="unit-size-observer" onResize={this.onUnitSizeResize}/>

                        {this.props.children}
                    </div>

                </OEPopover>
            </React.Fragment>
        );
    }
}

OENotePopover.defaultProps = Object.assign(
    {},
    OEToolbox.filterObject(OEPopover.defaultProps, null, ['position', 'rect', 'offset']),
    {
        buttonClassName:    'transparent-btn',
        headerSeparator:    false,
        backdrop:           true,
        backdropOptions:    {color: 'rgba(0, 0, 0, 0)'},
        moduleId:           '',
        contentClassName:   'note-popover',
        resizeable:         false,
        rectUpdateEnabled:  true
    }
);

OENotePopover.propTypes = Object.assign(
    {},
    OEToolbox.filterObject(OEPopover.propTypes, null, ['position', 'rect', 'offset']),
    {
        contentClassName:   PropTypes.string,
        id:                 PropTypes.number,
        resizeable:         PropTypes.bool,
        rectUpdateEnabled:  PropTypes.bool,
        onRectDidUpdate:    PropTypes.func,
        onResize:           PropTypes.func
    }
);

//
export default frefFromRef((connectModuleEnv((env) => {
    return {
        offset: {x: 0, y: -env.ui.capBar.edgeOffsets.top}
    };
})(frefToRef(OENotePopover))));